/**
 * Portal 관리자 로그인 전용 페이지
 * @description /admin/login — 관리자만, 로그인 후 /admin
 */

import { useState } from 'react';
import { useNavigate, getQueryParam } from '@shared/router';
import { useAuthStore } from '@/stores/authStore';
import { PORTAL_VERSION_FOOTER } from '@shared/config';
import { Input, Button } from '@shared/ui/primitives';
import { Eye, EyeOff, Key } from 'lucide-react';
import { usePasskey } from '@/hooks/usePasskey';
import { getAdminAuthStorage } from '@/utils/authStorage';
import { AuthLoginShell } from '@/features/auth/components/AuthLoginShell';
import { usePortalTranslation } from '@shared/i18n';

const inputClass =
  'w-full h-10 rounded-lg border-slate-200/80 bg-white/70 shadow-sm focus:border-primary/40 focus:ring-2 focus:ring-primary/15 transition-all duration-200';

export const AdminLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = usePortalTranslation('auth');
  const login = useAuthStore((state) => state.login);
  const { authenticate: authenticatePasskey, isLoading: isPasskeyLoading, error: passkeyError } = usePasskey();
  const storage = getAdminAuthStorage();
  const returnUrl = getQueryParam('returnUrl');

  const [adminUserId, setAdminUserId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await login({
        mode: 'portal-admin',
        adminUserId,
        password,
      });
      if (returnUrl) {
        const navigateToService = useAuthStore.getState().navigateToService;
        navigateToService(decodeURIComponent(returnUrl));
      } else {
        navigate({ to: '/admin' });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '로그인에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    if (!adminUserId) {
      setError('관리자 ID를 입력해주세요.');
      return;
    }
    setError(null);
    try {
      const result = await authenticatePasskey(adminUserId);
      storage.setToken(result.token);
      const user = {
        userType: 'portal-admin' as const,
        adminUserId: result.adminUserId,
        displayName: result.adminUserId === 'admin' ? 'Portal 관리자' : result.adminUserId,
        permissions: result.permissions,
      };
      storage.setUser(user);
      useAuthStore.setState({ realm: 'admin', isAuthenticated: true, user });
      if (returnUrl) {
        const navigateToService = useAuthStore.getState().navigateToService;
        navigateToService(decodeURIComponent(returnUrl));
      } else {
        navigate({ to: '/admin' });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Passkey 로그인에 실패했습니다.');
    }
  };

  return (
    <AuthLoginShell>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 tracking-tight">
          {t('adminLogin.title')}
        </h2>
        <p className="mt-1.5 text-sm text-slate-500">
          {t('adminLogin.subtitle')}
        </p>
      </div>
      {(error || passkeyError) && (
        <div className="mb-5 p-3 rounded-lg bg-red-50/90 border border-red-200/80 backdrop-blur-sm" role="alert">
          <p className="text-sm text-red-700 font-medium">{error || passkeyError}</p>
        </div>
      )}
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="space-y-1.5">
          <label htmlFor="adminUserId" className="block text-sm font-medium text-slate-700">
            {t('login.adminUserId')}
          </label>
          <Input
            id="adminUserId"
            type="text"
            placeholder="admin"
            value={adminUserId}
            onChange={(e) => setAdminUserId(e.target.value)}
            disabled={isLoading}
            className={inputClass}
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="password" className="block text-sm font-medium text-slate-700">
            {t('login.password')}
          </label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder={t('adminLogin.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              className={`${inputClass} pr-10`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              aria-label={showPassword ? t('adminLogin.hidePassword') : t('adminLogin.showPassword')}
            >
              {showPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
            </button>
          </div>
          <p className="text-xs text-slate-500">
            {t('adminLogin.passwordPolicy')}
          </p>
        </div>
        <Button
          type="submit"
          variant="default"
          disabled={isLoading || isPasskeyLoading}
          size="sm"
          className="w-full h-11 mt-1 rounded-lg font-medium shadow-md shadow-primary/15"
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              {t('login.submitting')}
            </span>
          ) : (
            t('login.submit')
          )}
        </Button>
        <div className="flex items-center gap-3 my-1">
          <div className="flex-1 h-px bg-slate-200/80" />
          <span className="text-xs text-slate-500">{t('login.or')}</span>
          <div className="flex-1 h-px bg-slate-200/80" />
        </div>
        <Button
          type="button"
          variant="outline"
          disabled={isLoading || isPasskeyLoading || !adminUserId}
          size="sm"
          onClick={handlePasskeyLogin}
          className="w-full h-11 rounded-lg font-medium bg-white/50"
        >
          {isPasskeyLoading ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              {t('login.passkeySubmitting')}
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Key className="w-4 h-4" />
              {t('login.passkey')}
            </span>
          )}
        </Button>
      </form>
      <p className="text-xs text-slate-500 text-center mt-6">
        {t('adminLogin.tenantLoginLink')}{' '}
        <a href="/login" className="text-primary hover:underline font-medium">
          /login
        </a>{' '}
        {t('adminLogin.tenantLoginSuffix')}
      </p>
      <p className="text-[11px] text-slate-400 text-center mt-2 tracking-wide">
        {PORTAL_VERSION_FOOTER}
      </p>
    </AuthLoginShell>
  );
};

AdminLoginPage.displayName = 'AdminLoginPage';
